/**
 * ISO 4217 active currency codes — the single source of truth shared by the
 * publish-time policy validator (`services/api`) and the dashboard rule editor
 * (`apps/dashboard`) so a merchant-authored currency cap key is checked against
 * the SAME list on both sides (drift would let the form accept a code the
 * server rejects, or vice-versa).
 *
 * Scope: this constrains MERCHANT-AUTHORED config (the `caps` / `auto_approve_caps`
 * keys on r05 / r07). The mandate verifier (`packages/mandate/src/verify.ts`)
 * deliberately stays shape-only (`/^[A-Z]{3}$/`) on the AGENT-DECLARED
 * `intent.currency` — an unknown agent currency is handled by each rule's
 * unlisted-currency disposition (r05 reject/allow, r07 fail-safe), so leniency
 * there is safe; the value of an allowlist is catching a merchant TYPO (e.g.
 * `EUO` for `EUR`) that would silently make a cap inert.
 *
 * List basis: ISO 4217 active codes (fund codes like `XAU`/`XDR` and the
 * `XXX`/`XTS` placeholders are intentionally EXCLUDED — a merchant settling in
 * gold or the "no currency" code is not a real config). Additive: a newly
 * adopted code is added here in one place and both consumers pick it up.
 */
/** Active ISO 4217 alphabetic currency codes (uppercase). */
export const ISO_4217_CODES = new Set([
    'AED', 'AFN', 'ALL', 'AMD', 'ANG', 'AOA', 'ARS', 'AUD', 'AWG', 'AZN',
    'BAM', 'BBD', 'BDT', 'BGN', 'BHD', 'BIF', 'BMD', 'BND', 'BOB', 'BOV',
    'BRL', 'BSD', 'BTN', 'BWP', 'BYN', 'BZD', 'CAD', 'CDF', 'CHE', 'CHF',
    'CHW', 'CLF', 'CLP', 'CNY', 'COP', 'COU', 'CRC', 'CUP', 'CVE', 'CZK',
    'DJF', 'DKK', 'DOP', 'DZD', 'EGP', 'ERN', 'ETB', 'EUR', 'FJD', 'FKP',
    'GBP', 'GEL', 'GHS', 'GIP', 'GMD', 'GNF', 'GTQ', 'GYD', 'HKD', 'HNL',
    'HTG', 'HUF', 'IDR', 'ILS', 'INR', 'IQD', 'IRR', 'ISK', 'JMD', 'JOD',
    'JPY', 'KES', 'KGS', 'KHR', 'KMF', 'KPW', 'KRW', 'KWD', 'KYD', 'KZT',
    'LAK', 'LBP', 'LKR', 'LRD', 'LSL', 'LYD', 'MAD', 'MDL', 'MGA', 'MKD',
    'MMK', 'MNT', 'MOP', 'MRU', 'MUR', 'MVR', 'MWK', 'MXN', 'MXV', 'MYR',
    'MZN', 'NAD', 'NGN', 'NIO', 'NOK', 'NPR', 'NZD', 'OMR', 'PAB', 'PEN',
    'PGK', 'PHP', 'PKR', 'PLN', 'PYG', 'QAR', 'RON', 'RSD', 'RUB', 'RWF',
    'SAR', 'SBD', 'SCR', 'SDG', 'SEK', 'SGD', 'SHP', 'SLE', 'SOS', 'SRD',
    'SSP', 'STN', 'SVC', 'SYP', 'SZL', 'THB', 'TJS', 'TMT', 'TND', 'TOP',
    'TRY', 'TTD', 'TWD', 'TZS', 'UAH', 'UGX', 'USD', 'USN', 'UYI', 'UYU',
    'UYW', 'UZS', 'VED', 'VES', 'VND', 'VUV', 'WST', 'XAF', 'XCD', 'XCG',
    'XOF', 'XPF', 'YER', 'ZAR', 'ZMW', 'ZWG',
]);
/**
 * True when `code` is an active ISO 4217 alphabetic currency code. `Set.has`
 * is prototype-safe — `__proto__` / `constructor` / `toString` all return
 * `false` — so this doubles as the prototype-pollution guard on a caps key map.
 */
export function isIso4217(code) {
    return ISO_4217_CODES.has(code);
}
//# sourceMappingURL=currency.js.map